import React, { useCallback, useEffect, useState } from "react";
import { C } from "../reporting/reportingStyles";
import { RCard, TablePagination } from "../reporting/ReportingShared";
import { getAuditLogs } from "../../utils/auditLogApi";

/**
 * "Who changed this, when, and what was it before" — for anything written to the
 * platform-wide AuditLogs table (marketing campaigns, wallet card settings, ...).
 *
 * Generic on purpose: the record is one table, and the screens differ only by a filter and
 * by how they label fields and actions. Each caller passes its own `actionLabels` /
 * `fieldLabels`; anything unlabelled falls back to the raw key so a new field is still
 * visible rather than hidden.
 *
 * Pages on the SERVER — the table is pruned after retention, but a campaign edited daily
 * still has more history than a screen should try to render at once.
 *
 * To refresh after a change made without leaving the page, give it a changing `key` and
 * let React remount it (which also returns it to page 1, where the new entry is).
 */

/** Row bookkeeping, never worth a diff line. */
const NOISE_FIELDS = new Set(["id", "merchantId", "revision", "createdAt", "updatedAt", "deletedAt"]);

/** Above this many changed fields the diff is collapsed (a creation records the whole row). */
const COLLAPSE_ABOVE = 6;

const notSet = <em style={{ color: C.textLight }}>not set</em>;

const renderValue = (v) => {
    if (v === null || v === undefined || v === "") return notSet;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString();
    if (Array.isArray(v)) return v.length ? v.join(", ") : <em style={{ color: C.textLight }}>none</em>;
    if (typeof v === "object") return <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>;
    return String(v);
};

const DiffRow = ({ field, before, after, fieldLabels, resolve }) => {
    const render = (v) => {
        const resolved = resolve?.(field, v);
        if (resolved !== undefined && resolved !== null) return resolved;
        return renderValue(v);
    };
    return (
        <div style={{ fontSize: 12, color: C.textMed }}>
            <strong style={{ color: C.text }}>{fieldLabels?.[field] || field}:</strong>{" "}
            {render(before)} <span style={{ color: C.textLight }}>→</span> {render(after)}
        </div>
    );
};

/**
 * `metadata` is context rather than a diff — counters from a job, the kind of a push — so
 * it renders as a compact "key · value" line instead of before → after rows.
 */
const MetadataLine = ({ metadata, fieldLabels }) => {
    const entries = Object.entries(metadata || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (!entries.length) return null;
    return (
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: 12, color: C.textMed }}>
            {entries.map(([k, v]) => (
                <span key={k}>
                    <span style={{ color: C.textLight }}>{fieldLabels?.[k] || k}</span>{" "}
                    <strong style={{ color: C.text }}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</strong>
                </span>
            ))}
        </div>
    );
};

export const AuditEntry = ({ row, actionLabels, fieldLabels, resolve }) => {
    const before = row.previousValues || {};
    const after = row.newValues || {};
    const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
        .filter((f) => !NOISE_FIELDS.has(f));

    const [expanded, setExpanded] = useState(false);
    const collapsible = fields.length > COLLAPSE_ABOVE;
    const shown = collapsible && !expanded ? [] : fields;

    const actor = row.source === "job" ? "system" : row.actorEmail || "unknown user";
    const title = actionLabels?.[row.action] || row.action;

    return (
        <div style={{ padding: "12px 0", borderBottom: `1px solid ${C.borderLight}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>
                    {title}
                    {row.entityName ? <span style={{ color: C.textMed, fontWeight: 400 }}> · {row.entityName}</span> : null}
                </div>
                <div style={{ fontSize: 12, color: C.textLight }}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"} · {actor}
                </div>
            </div>
            {row.reason && <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>“{row.reason}”</div>}
            {fields.length > 0 && (
                <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: `2px solid ${C.borderLight}` }}>
                    {shown.map((f) => (
                        <DiffRow key={f} field={f} before={before[f]} after={after[f]} fieldLabels={fieldLabels} resolve={resolve} />
                    ))}
                    {collapsible && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            style={{
                                marginTop: shown.length ? 6 : 0, padding: 0, border: "none", background: "none",
                                color: C.coral, fontSize: 12, fontWeight: 600, cursor: "pointer",
                            }}
                        >
                            {expanded ? "Hide the details" : `Show all ${fields.length} fields`}
                        </button>
                    )}
                </div>
            )}
            <MetadataLine metadata={row.metadata} fieldLabels={fieldLabels} />
        </div>
    );
};

/**
 * @param {object} props
 * @param {number|string} props.merchantId   required — history is per merchant
 * @param {string} [props.module]            "marketing" | "wallet" | ...
 * @param {string} [props.entityType]        e.g. "push_campaign"; required when entityId is given
 * @param {number} [props.entityId]
 * @param {string[]} [props.entityTypes]     several types for one panel (client-side OR: one request
 *                                           per type is avoided by leaving entityType unset and
 *                                           filtering rows here)
 * @param {object} [props.actionLabels]      action → label
 * @param {object} [props.fieldLabels]       field key → label (diff rows and metadata)
 * @param {(field: string, value: any) => React.ReactNode|undefined} [props.resolve]
 * @param {string} [props.title]
 * @param {number} [props.defaultLimit]
 */
const AuditLogPanel = ({
    merchantId,
    module,
    entityType,
    entityId,
    entityTypes,
    actionLabels,
    fieldLabels,
    resolve,
    title,
    defaultLimit = 20,
}) => {
    const [log, setLog] = useState({ rows: [], total: 0, totalPages: 0 });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(defaultLimit);
    const [isLoading, setIsLoading] = useState(true);

    const load = useCallback(async () => {
        if (!merchantId || merchantId === "all") return;
        setIsLoading(true);
        const params = { merchantId, page, limit };
        if (module) params.module = module;
        if (entityType) params.entityType = entityType;
        if (entityType && entityId) params.entityId = entityId;
        const data = await getAuditLogs(params);
        setLog(data);
        setIsLoading(false);
    }, [merchantId, module, entityType, entityId, page, limit]);

    useEffect(() => { load(); }, [load]);

    const total = log.total || 0;
    const rows = Array.isArray(entityTypes) && entityTypes.length
        ? (log.rows || []).filter((r) => entityTypes.includes(r.entityType))
        : (log.rows || []);

    return (
        <RCard title={title || `📜 Change history · ${total} entr${total === 1 ? "y" : "ies"}`}>
            {isLoading && rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>Loading…</div>
            ) : rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>
                    {page > 1 ? "Nothing further back." : "No changes recorded yet."}
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {rows.map((row) => (
                        <AuditEntry
                            key={row.id}
                            row={row}
                            actionLabels={actionLabels}
                            fieldLabels={fieldLabels}
                            resolve={resolve}
                        />
                    ))}
                    <TablePagination
                        total={total}
                        page={page}
                        limit={limit}
                        onPageChange={setPage}
                        onLimitChange={(l) => { setLimit(l); setPage(1); }}
                    />
                </div>
            )}
        </RCard>
    );
};

export default AuditLogPanel;
